import type { WorkOrderChangePriceItem } from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { Button, Checkbox, ContextMenu, IconKebab, Loader } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useMenuItemsByRowType } from './hooks/useMenuItemsByRowType';
import { ObjectTotalsButton } from './ObjectTotalsButton';

const getItemLabel = R.prop('label');
const getItemSubMenu = R.prop('menu');

interface IWorkOrderChangeMenuRenderer extends ICellRendererParams<WorkOrderChangePriceItem> {
    openModalHandler: (type: any) => void;
    isActionsDisabled: boolean;
    checkedRows: Record<string, WorkOrderChangePriceItem>;
    checkRowHandler: ({ id, data }: { id: string; data: WorkOrderChangePriceItem | null | undefined }) => void;
}

export const WorkOrderChangeMenuRenderer = (props: IWorkOrderChangeMenuRenderer) => {
    const { data, openModalHandler, isActionsDisabled, checkRowHandler, checkedRows } = props;
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const ref = useRef(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const { menuItems, menuGroups } = useMenuItemsByRowType({
        rowData: data,
        openModalHandler,
    });

    const handleItemOnClick = useCallback(
        (item: (typeof menuItems)[number]) => () => {
            item?.action?.();
            setIsOpen(false);
        },
        [],
    );

    const kebabDisabled = useMemo(
        () =>
            !!data?.isExcludedPrice || isActionsDisabled || !!data?.isBlockedPrice || !workOrderChangeUerRules?.canEdit,
        [data?.isBlockedPrice, data?.isExcludedPrice, isActionsDisabled, workOrderChangeUerRules?.canEdit],
    );

    const handleCheckbox = useCallback(
        (id: string) =>
            ({ checked }: { checked: boolean }) => {
                if (checked) {
                    checkRowHandler({ id, data });
                } else {
                    checkRowHandler({ id, data: null });
                }
            },
        [checkRowHandler, data],
    );

    const checkboxRemoveDisabled = useMemo(
        () => data?.hasAcceptedCost !== null || kebabDisabled || !workOrderChangeUerRules?.canEdit,
        [data?.hasAcceptedCost, kebabDisabled, workOrderChangeUerRules?.canEdit],
    );

    const isRootOrOtherGroup = useMemo(
        () =>
            [WorkOrderPriceTypeEnum.RootObject, WorkOrderPriceTypeEnum.OtherGroup].includes(
                data?.type as WorkOrderPriceTypeEnum,
            ),
        [data?.type],
    );

    return !data ? (
        <Loader />
    ) : (
        <>
            {data?.type === WorkOrderPriceTypeEnum.RootObject && <ObjectTotalsButton data={data} />}
            {!isRootOrOtherGroup && (
                <>
                    <Checkbox
                        checked={!!checkedRows[data.clientId]}
                        onChange={handleCheckbox(data.clientId)}
                        disabled={checkboxRemoveDisabled}
                    />
                    <Button
                        disabled={kebabDisabled}
                        iconLeft={IconKebab}
                        onlyIcon
                        view="clear"
                        size="xs"
                        onClick={handleOnClick}
                        ref={ref}
                        style={{
                            marginLeft: '6px',
                        }}
                    />
                    {Boolean(menuItems?.length) && (
                        <ContextMenu
                            isOpen={isOpen}
                            items={menuItems}
                            groups={menuGroups}
                            getItemLabel={getItemLabel}
                            anchorRef={ref}
                            direction="downStartLeft"
                            getItemOnClick={handleItemOnClick}
                            onClickOutside={handleOnClick}
                            size="s"
                            getItemSubMenu={getItemSubMenu}
                        />
                    )}
                </>
            )}
        </>
    );
};

WorkOrderChangeMenuRenderer.displayName = 'WorkOrderChangeMenuRenderer';
